/*
 *  Copyright (c) Qwoach LLC 2026. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 */

import {GenogramConnectionCategoryEnum} from "@shared/Genogram/GenogramConnectionCategoryEnum";
import {
	GenogramCustomConnectionCenterSymbolEnum,
	GenogramCustomConnectionDecorationEnum,
	GenogramCustomConnectionDirectionEnum,
	GenogramCustomConnectionPatternEnum,
} from "@shared/Genogram/GenogramCustomConnectionEnum";

const PREVIEW_WIDTH = 340 / 2
const PREVIEW_HEIGHT = 60 / 2
const DEFAULT_COLOR = "#D94841"
const DEFAULT_SOCIAL_SIDE_COLOR = "rgb(153, 153, 153)"
const DEFAULT_SOCIAL_SIDE_COLOR_SELECTED = "rgb(100, 100, 100)"
const ARROW_BASE_SIZE = 4
const STANDARD_CONNECTION_LINE_WIDTH = 4
const STANDARD_PARALLEL_LINE_SPACING = 8
const WAVE_PARALLEL_LINE_SPACING = 10
const CHAIN_PARALLEL_LINE_SPACING = 9
const STANDARD_SELECTED_LINE_WIDTH_MULTIPLIER = 1.6
const STANDARD_CONNECTION_LINE_CAP = "butt"
const STANDARD_CONNECTION_LINE_JOIN = "miter"
const STANDARD_SELECTED_COLOR_MULTIPLIER = 0.8
const SYMBOL_SCALE_FACTOR = 1.5
const SYMBOL_HALF_SIZE = 6 * SYMBOL_SCALE_FACTOR
const SYMBOL_CIRCLE_RADIUS = 5 * SYMBOL_SCALE_FACTOR
const SYMBOL_DOT_RADIUS = 1.6 * SYMBOL_SCALE_FACTOR
const SYMBOL_TRIANGLE_HALF_WIDTH = 5 * SYMBOL_SCALE_FACTOR
const SYMBOL_TRIANGLE_HALF_HEIGHT = 4.5 * SYMBOL_SCALE_FACTOR
const SYMBOL_STAR_OUTER_RADIUS = 5.5 * SYMBOL_SCALE_FACTOR
const SYMBOL_STAR_INNER_RADIUS = 2.6 * SYMBOL_SCALE_FACTOR
const SYMBOL_HEART_SCALE = 0.9 * SYMBOL_SCALE_FACTOR
const SYMBOL_KNOCKOUT_EXTRA_PADDING = 2

const CenterSymbolRenderModeEnum = Object.freeze({
	OVERLAY: "overlay",
	KNOCKOUT_OUTLINE: "knockoutOutline",
	FILLED: "filled",
})

function normalizeSelectValue(value, fallback) {
	if(value != null && typeof value === "object" && value.value != null){
		return value.value
	}
	return value ?? fallback
}

function normalizeDecorationValue(value) {
	let normalizedValue = normalizeSelectValue(value, GenogramCustomConnectionDecorationEnum.NONE)
	switch (normalizedValue) {
		case "bar":
			return GenogramCustomConnectionDecorationEnum.PERPENDICULAR_TICKS
		case "doubleBar":
			return GenogramCustomConnectionDecorationEnum.DOUBLE_TICKS
		case "hooks":
			return GenogramCustomConnectionDecorationEnum.SLASHES
		case "fork":
			return GenogramCustomConnectionDecorationEnum.CROSS_TICKS
		case "endDot":
			return GenogramCustomConnectionDecorationEnum.DOTS_OVERLAY
		default:
			return normalizedValue
	}
}

export default class GenogramCustomConnectionRenderManager {

	getPreviewSize() {
		return {
			width: PREVIEW_WIDTH,
			height: PREVIEW_HEIGHT,
		}
	}

	normalizeRenderConfig(renderConfig = {}) {
		let lineCount = Number(normalizeSelectValue(renderConfig.lineCount, 1))
		if(!Number.isFinite(lineCount)){
			lineCount = 1
		}
		lineCount = Math.max(1, Math.min(3, Math.round(lineCount)))
		let normalizedConfig = {
			color: DEFAULT_COLOR,
			lineCount: 1,
			pattern: GenogramCustomConnectionPatternEnum.SOLID,
			centerSymbol: GenogramCustomConnectionCenterSymbolEnum.NONE,
			decoration: GenogramCustomConnectionDecorationEnum.NONE,
			direction: GenogramCustomConnectionDirectionEnum.NONE,
			...renderConfig,
		}
		normalizedConfig.lineCount = lineCount
		normalizedConfig.pattern = normalizeSelectValue(normalizedConfig.pattern, GenogramCustomConnectionPatternEnum.SOLID)
		normalizedConfig.centerSymbol = normalizeSelectValue(normalizedConfig.centerSymbol, GenogramCustomConnectionCenterSymbolEnum.NONE)
		normalizedConfig.decoration = normalizeDecorationValue(normalizedConfig.decoration)
		normalizedConfig.direction = normalizeSelectValue(normalizedConfig.direction, GenogramCustomConnectionDirectionEnum.NONE)
		return normalizedConfig
	}

	buildPreviewDataUrl(renderConfig = {}, options = {}) {
		let canvas = document.createElement("canvas")
		let {width, height} = this.getPreviewSize()
		canvas.width = options.width || width
		canvas.height = options.height || height
		let context = canvas.getContext("2d")
		if(!context){
			return ""
		}
		this.drawPreview(context, renderConfig, {
			connectionCategory: options.connectionCategory,
			width: canvas.width,
			height: canvas.height,
			isSelected: !!options.isSelected,
		})
		return canvas.toDataURL("image/png")
	}

	drawPreview(context, renderConfig = {}, options = {}) {
		let {
			width = PREVIEW_WIDTH,
			height = PREVIEW_HEIGHT,
			isSelected = false,
			connectionCategory = GenogramConnectionCategoryEnum.EMOTIONAL,
		} = options
		if(connectionCategory === GenogramConnectionCategoryEnum.SOCIAL){
			return this.drawSocialPreview(context, renderConfig, {width, height, isSelected})
		}
		let startX = 12
		let endX = Math.max(width - 12, startX + 20)
		let centerY = Math.round(height / 2)
		this.drawConnectionSegment(context, renderConfig, {
			x1: startX,
			y1: centerY,
			x2: endX,
			y2: centerY,
			isSelected,
		})
	}

	drawSocialPreview(context, renderConfig = {}, options = {}) {
		let {width = PREVIEW_WIDTH, height = PREVIEW_HEIGHT, isSelected = false} = options
		let leftX = 22
		let rightX = Math.max(width - 22, leftX + 28)
		let topY = 6
		let lineY = Math.round(height * 0.68)
		this.drawSocialSideSegment(context, leftX, topY, lineY, isSelected)
		this.drawConnectionSegment(context, renderConfig, {
			x1: leftX,
			y1: lineY,
			x2: rightX,
			y2: lineY,
			isSelected,
		})
		this.drawSocialSideSegment(context, rightX, topY, lineY, isSelected)
	}

	drawSocialSideSegment(context, x, topY, bottomY, isSelected = false) {
		context.save()
		context.lineCap = STANDARD_CONNECTION_LINE_CAP
		context.lineJoin = STANDARD_CONNECTION_LINE_JOIN
		context.strokeStyle = isSelected ? DEFAULT_SOCIAL_SIDE_COLOR_SELECTED : DEFAULT_SOCIAL_SIDE_COLOR
		context.lineWidth = STANDARD_CONNECTION_LINE_WIDTH * (isSelected ? STANDARD_SELECTED_LINE_WIDTH_MULTIPLIER : 1)
		context.beginPath()
		context.moveTo(x, topY)
		context.lineTo(x, bottomY)
		context.stroke()
		context.restore()
	}

	drawConnectionSegment(context, renderConfig = {}, options = {}) {
		let config = this.normalizeRenderConfig(renderConfig)
		let {x1, y1, x2, y2, isSelected = false, showDirectionalArrow = true} = options
		if(x1 == null || y1 == null || x2 == null || y2 == null){
			return
		}

		let angle = Math.atan2(y2 - y1, x2 - x1)
		let length = Math.hypot(x2 - x1, y2 - y1)
		let offsets = this.buildLineOffsets(config.lineCount, config.pattern)

		context.save()
		context.translate(x1, y1)
		context.rotate(angle)
		context.lineCap = STANDARD_CONNECTION_LINE_CAP
		context.lineJoin = STANDARD_CONNECTION_LINE_JOIN
		let connectionStyle = this.buildConnectionStyle(config, isSelected)
		this.applyConnectionStyle(context, connectionStyle)

		let shouldDrawDirectionalArrow = showDirectionalArrow && config.direction === GenogramCustomConnectionDirectionEnum.DIRECTIONAL
		let arrowSize = shouldDrawDirectionalArrow ? this.getDirectionalArrowSize(connectionStyle) : 0
		let lineShortening = shouldDrawDirectionalArrow ? this.getDirectionalLineShortening(config.lineCount, offsets, arrowSize) : 0
		let trackLength = shouldDrawDirectionalArrow ? Math.max(length - lineShortening, 0) : length

		for(let offset of offsets){
			this.drawPattern(context, config.pattern, trackLength, offset)
		}

		this.drawCenterSymbol(context, config.centerSymbol, length / 2, 0)
		this.drawDecoration(context, config.decoration, length)
		if(shouldDrawDirectionalArrow){
			this.drawDirectionalArrow(context, length, connectionStyle)
		}
		context.restore()
	}

	buildLineOffsets(lineCount = 1, pattern = GenogramCustomConnectionPatternEnum.SOLID) {
		let spacing = this.getPatternSpacing(pattern)
		if(lineCount <= 1){
			return [0]
		}
		if(lineCount === 2){
			return [-spacing / 2, spacing / 2]
		}
		return [-spacing, 0, spacing]
	}

	getPatternSpacing(pattern) {
		switch (pattern) {
			case GenogramCustomConnectionPatternEnum.ZIGZAG:
			case GenogramCustomConnectionPatternEnum.WAVE:
			case GenogramCustomConnectionPatternEnum.SQUARE_WAVE:
			case GenogramCustomConnectionPatternEnum.PULSE:
				return WAVE_PARALLEL_LINE_SPACING
			case GenogramCustomConnectionPatternEnum.CHAIN:
				return CHAIN_PARALLEL_LINE_SPACING
			default:
				return STANDARD_PARALLEL_LINE_SPACING
		}
	}

	drawPattern(context, pattern, length, offsetY) {
		context.save()
		context.translate(0, offsetY)
		switch (pattern) {
			case GenogramCustomConnectionPatternEnum.DASHED:
				this.drawDashedLine(context, length)
				break
			case GenogramCustomConnectionPatternEnum.DOTTED:
				this.drawDottedLine(context, length)
				break
			case GenogramCustomConnectionPatternEnum.ZIGZAG:
				this.drawZigZagLine(context, length, 5, 4)
				break
			case GenogramCustomConnectionPatternEnum.WAVE:
				this.drawWaveLine(context, length, 10, 4)
				break
			case GenogramCustomConnectionPatternEnum.SQUARE_WAVE:
				this.drawSquareWaveLine(context, length, 10, 4)
				break
			case GenogramCustomConnectionPatternEnum.BROKEN:
				this.drawBrokenLine(context, length)
				break
			case GenogramCustomConnectionPatternEnum.CHAIN:
				this.drawChainLine(context, length)
				break
			case GenogramCustomConnectionPatternEnum.PULSE:
				this.drawPulseLine(context, length)
				break
			default:
				this.drawSolidLine(context, length)
		}
		context.restore()
	}

	drawSolidLine(context, length) {
		context.beginPath()
		context.moveTo(0, 0)
		context.lineTo(length, 0)
		context.stroke()
	}

	drawDashedLine(context, length) {
		context.save()
		context.setLineDash([10, 7])
		this.drawSolidLine(context, length)
		context.restore()
	}

	drawDottedLine(context, length) {
		context.save()
		context.setLineDash([2, 6])
		this.drawSolidLine(context, length)
		context.restore()
	}

	drawBrokenLine(context, length) {
		let gapSize = Math.min(22, Math.max(14, length * 0.18))
		let halfLength = Math.max((length - gapSize) / 2, 0)
		context.beginPath()
		context.moveTo(0, 0)
		context.lineTo(halfLength, 0)
		context.moveTo(halfLength + gapSize, 0)
		context.lineTo(length, 0)
		context.stroke()
	}

	drawChainLine(context, length) {
		let linkRadiusX = 7
		let linkRadiusY = 4
		let overlap = 4
		let step = Math.max(linkRadiusX * 2 - overlap, 1)
		let startX = linkRadiusX
		for(let x = startX, index = 0; x < length - linkRadiusX / 2; x += step, index++){
			let offsetY = index % 2 === 0 ? -1.2 : 1.2
			context.beginPath()
			context.ellipse(x, offsetY, linkRadiusX, linkRadiusY, 0, 0, 2 * Math.PI)
			context.stroke()
		}
	}

	drawPulseLine(context, length) {
		let centerX = length / 2
		let pulseWidth = Math.min(18, length / 4)
		context.beginPath()
		context.moveTo(0, 0)
		context.lineTo(Math.max(centerX - pulseWidth, 0), 0)
		context.lineTo(centerX - pulseWidth / 3, 0)
		context.lineTo(centerX, -8)
		context.lineTo(centerX + pulseWidth / 3, 8)
		context.lineTo(Math.min(centerX + pulseWidth, length), 0)
		context.lineTo(length, 0)
		context.stroke()
	}

	drawZigZagLine(context, length, step = 5, amplitude = 4) {
		context.beginPath()
		context.moveTo(0, 0)
		let isUp = true
		for(let x = step; x < length; x += step){
			context.lineTo(x, isUp ? -amplitude : amplitude)
			isUp = !isUp
		}
		context.lineTo(length, 0)
		context.stroke()
	}

	drawWaveLine(context, length, step = 10, amplitude = 4) {
		context.beginPath()
		context.moveTo(0, 0)
		for(let x = 0; x < length; x += step){
			let ctrlX = x + step / 2
			let endX = Math.min(x + step, length)
			let ctrlY = ((x / step) % 2 === 0) ? -amplitude : amplitude
			context.quadraticCurveTo(ctrlX, ctrlY, endX, 0)
		}
		context.stroke()
	}

	drawSquareWaveLine(context, length, step = 10, amplitude = 4) {
		context.beginPath()
		context.moveTo(0, 0)
		let x = 0
		let isUp = true
		while(x < length){
			let nextX = Math.min(x + step / 2, length)
			context.lineTo(nextX, 0)
			context.lineTo(nextX, isUp ? -amplitude : amplitude)
			x = Math.min(nextX + step / 2, length)
			context.lineTo(x, isUp ? -amplitude : amplitude)
			context.lineTo(x, 0)
			isUp = !isUp
		}
		context.stroke()
	}

	drawCenterSymbol(context, centerSymbol, x, y) {
		if(centerSymbol === GenogramCustomConnectionCenterSymbolEnum.NONE){
			return
		}

		context.save()
		context.translate(x, y)
		context.fillStyle = context.strokeStyle
		let renderMode = this.getCenterSymbolRenderMode(centerSymbol)
		if(renderMode === CenterSymbolRenderModeEnum.KNOCKOUT_OUTLINE){
			this.eraseCenterSymbolArea(context, centerSymbol)
		}

		context.beginPath()
		this.drawCenterSymbolPath(context, centerSymbol)
		if(renderMode === CenterSymbolRenderModeEnum.FILLED){
			context.fill()
		} else {
			context.stroke()
		}
		context.restore()
	}

	getCenterSymbolRenderMode(centerSymbol) {
		switch (centerSymbol) {
			case GenogramCustomConnectionCenterSymbolEnum.CIRCLE:
			case GenogramCustomConnectionCenterSymbolEnum.DIAMOND:
			case GenogramCustomConnectionCenterSymbolEnum.TRIANGLE:
			case GenogramCustomConnectionCenterSymbolEnum.STAR:
			case GenogramCustomConnectionCenterSymbolEnum.HEART:
			case GenogramCustomConnectionCenterSymbolEnum.BOLT:
				return CenterSymbolRenderModeEnum.KNOCKOUT_OUTLINE
			case GenogramCustomConnectionCenterSymbolEnum.FILLED_CIRCLE:
			case GenogramCustomConnectionCenterSymbolEnum.FILLED_DIAMOND:
				return CenterSymbolRenderModeEnum.FILLED
			default:
				return CenterSymbolRenderModeEnum.OVERLAY
		}
	}

	eraseCenterSymbolArea(context, centerSymbol) {
		context.save()
		context.globalCompositeOperation = "destination-out"
		context.lineJoin = "round"
		context.lineCap = "round"
		context.lineWidth += SYMBOL_KNOCKOUT_EXTRA_PADDING * 2
		context.beginPath()
		this.drawCenterSymbolPath(context, centerSymbol)
		context.fill()
		context.stroke()
		context.restore()
	}

	drawCenterSymbolPath(context, centerSymbol) {
		switch (centerSymbol) {
			case GenogramCustomConnectionCenterSymbolEnum.SLASH:
				context.moveTo(-SYMBOL_HALF_SIZE, SYMBOL_HALF_SIZE)
				context.lineTo(SYMBOL_HALF_SIZE, -SYMBOL_HALF_SIZE)
				break
			case GenogramCustomConnectionCenterSymbolEnum.CROSS:
				context.moveTo(-SYMBOL_HALF_SIZE, -SYMBOL_HALF_SIZE)
				context.lineTo(SYMBOL_HALF_SIZE, SYMBOL_HALF_SIZE)
				context.moveTo(-SYMBOL_HALF_SIZE, SYMBOL_HALF_SIZE)
				context.lineTo(SYMBOL_HALF_SIZE, -SYMBOL_HALF_SIZE)
				break
			case GenogramCustomConnectionCenterSymbolEnum.CIRCLE:
			case GenogramCustomConnectionCenterSymbolEnum.FILLED_CIRCLE:
				context.arc(0, 0, SYMBOL_CIRCLE_RADIUS, 0, 2 * Math.PI, false)
				break
			case GenogramCustomConnectionCenterSymbolEnum.DIAMOND:
			case GenogramCustomConnectionCenterSymbolEnum.FILLED_DIAMOND:
				context.moveTo(0, -SYMBOL_HALF_SIZE)
				context.lineTo(SYMBOL_HALF_SIZE, 0)
				context.lineTo(0, SYMBOL_HALF_SIZE)
				context.lineTo(-SYMBOL_HALF_SIZE, 0)
				context.closePath()
				break
			case GenogramCustomConnectionCenterSymbolEnum.TRIANGLE:
				context.moveTo(0, -SYMBOL_TRIANGLE_HALF_HEIGHT)
				context.lineTo(SYMBOL_TRIANGLE_HALF_WIDTH, SYMBOL_TRIANGLE_HALF_HEIGHT)
				context.lineTo(-SYMBOL_TRIANGLE_HALF_WIDTH, SYMBOL_TRIANGLE_HALF_HEIGHT)
				context.closePath()
				break
			case GenogramCustomConnectionCenterSymbolEnum.STAR:
				this.drawStarPath(context, 0, 0, SYMBOL_STAR_OUTER_RADIUS, SYMBOL_STAR_INNER_RADIUS)
				break
			case GenogramCustomConnectionCenterSymbolEnum.EXCLAMATION:
				context.moveTo(0, -SYMBOL_HALF_SIZE)
				context.lineTo(0, 2.5)
				context.moveTo(0, 5)
				context.arc(0, 5, SYMBOL_DOT_RADIUS, 0, 2 * Math.PI, false)
				break
			case GenogramCustomConnectionCenterSymbolEnum.QUESTION:
				context.moveTo(-4.5, -3.5)
				context.quadraticCurveTo(0, -7, 4.5, -3.5)
				context.quadraticCurveTo(4.5, -0.5, 0, 2)
				context.lineTo(0, 4)
				context.moveTo(0, 6.5)
				context.arc(0, 6.5, SYMBOL_DOT_RADIUS, 0, 2 * Math.PI, false)
				break
			case GenogramCustomConnectionCenterSymbolEnum.HEART:
				this.drawHeartPath(context, 0, -1, SYMBOL_HEART_SCALE)
				break
			case GenogramCustomConnectionCenterSymbolEnum.BOLT:
				context.moveTo(-3, -SYMBOL_HALF_SIZE)
				context.lineTo(2, -1)
				context.lineTo(-1, -1)
				context.lineTo(3, SYMBOL_HALF_SIZE)
				context.lineTo(-2, 1)
				context.lineTo(1, 1)
				break
		}
	}

	drawDecoration(context, decoration, length) {
		if(decoration === GenogramCustomConnectionDecorationEnum.NONE){
			return
		}

		context.save()
		switch (decoration) {
			case GenogramCustomConnectionDecorationEnum.PERPENDICULAR_TICKS:
				this.drawPerpendicularTicks(context, length)
				break
			case GenogramCustomConnectionDecorationEnum.CROSS_TICKS:
				this.drawCrossTicks(context, length)
				break
			case GenogramCustomConnectionDecorationEnum.SLASHES:
				this.drawSlashes(context, length)
				break
			case GenogramCustomConnectionDecorationEnum.DOUBLE_TICKS:
				this.drawDoubleTicks(context, length)
				break
			case GenogramCustomConnectionDecorationEnum.DIAMONDS:
				this.drawRepeatedDiamonds(context, length)
				break
			case GenogramCustomConnectionDecorationEnum.DOTS_OVERLAY:
				this.drawDotsOverlay(context, length)
				break
		}
		context.restore()
	}

	getDecorationSpacing() {
		return 18
	}

	drawPerpendicularTicks(context, length) {
		let spacing = this.getDecorationSpacing()
		let halfSize = 5
		context.beginPath()
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.moveTo(x, -halfSize)
			context.lineTo(x, halfSize)
		}
		context.stroke()
	}

	drawCrossTicks(context, length) {
		let spacing = this.getDecorationSpacing()
		let halfSize = 4
		context.beginPath()
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.moveTo(x - halfSize, -halfSize)
			context.lineTo(x + halfSize, halfSize)
			context.moveTo(x - halfSize, halfSize)
			context.lineTo(x + halfSize, -halfSize)
		}
		context.stroke()
	}

	drawSlashes(context, length) {
		let spacing = this.getDecorationSpacing()
		let halfWidth = 4
		let halfHeight = 5
		context.beginPath()
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.moveTo(x - halfWidth, halfHeight)
			context.lineTo(x + halfWidth, -halfHeight)
		}
		context.stroke()
	}

	drawDoubleTicks(context, length) {
		let spacing = this.getDecorationSpacing() + 2
		let halfSize = 5
		let gap = 3
		context.beginPath()
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.moveTo(x - gap, -halfSize)
			context.lineTo(x - gap, halfSize)
			context.moveTo(x + gap, -halfSize)
			context.lineTo(x + gap, halfSize)
		}
		context.stroke()
	}

	drawRepeatedDiamonds(context, length) {
		let spacing = this.getDecorationSpacing() + 4
		let size = 4
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.beginPath()
			context.moveTo(x, -size)
			context.lineTo(x + size, 0)
			context.lineTo(x, size)
			context.lineTo(x - size, 0)
			context.closePath()
			context.stroke()
		}
	}

	drawDotsOverlay(context, length) {
		let spacing = this.getDecorationSpacing()
		let radius = 2.6
		for(let x = spacing; x < length - spacing / 2; x += spacing){
			context.beginPath()
			context.arc(x, 0, radius, 0, 2 * Math.PI, false)
			context.fill()
		}
	}

	getDirectionalArrowSize(connectionStyle = {}) {
		return ARROW_BASE_SIZE * (connectionStyle.lineWidth || STANDARD_CONNECTION_LINE_WIDTH)
	}

	buildConnectionStyle(config = {}, isSelected = false) {
		let strokeStyle = config.color || DEFAULT_COLOR
		return {
			strokeStyle,
			strokeStyleSelected: this.getSelectedConnectionColor(strokeStyle),
			lineWidth: STANDARD_CONNECTION_LINE_WIDTH,
			isSelected,
		}
	}

	applyConnectionStyle(context, style = {}) {
		let strokeStyle = (style.isSelected ? style.strokeStyleSelected : style.strokeStyle) || context.strokeStyle
		context.strokeStyle = strokeStyle
		context.fillStyle = strokeStyle
		context.lineWidth = style.lineWidth || context.lineWidth
		if(style.isSelected){
			context.lineWidth = context.lineWidth * STANDARD_SELECTED_LINE_WIDTH_MULTIPLIER
		}
	}

	getSelectedConnectionColor(color) {
		let rgbColor = this.parseColorToRgb(color)
		if(!rgbColor){
			return color
		}
		let shadedColor = {
			r: this.clampRgbChannel(rgbColor.r * STANDARD_SELECTED_COLOR_MULTIPLIER),
			g: this.clampRgbChannel(rgbColor.g * STANDARD_SELECTED_COLOR_MULTIPLIER),
			b: this.clampRgbChannel(rgbColor.b * STANDARD_SELECTED_COLOR_MULTIPLIER),
		}
		return `rgb(${shadedColor.r}, ${shadedColor.g}, ${shadedColor.b})`
	}

	parseColorToRgb(color) {
		if(typeof color !== "string"){
			return null
		}
		if(color.startsWith("#")){
			return this.parseHexColorToRgb(color)
		}
		if(color.startsWith("rgb(")){
			return this.parseRgbColorToRgb(color)
		}
		return null
	}

	parseHexColorToRgb(color) {
		let normalizedColor = color.slice(1)
		if(normalizedColor.length === 3){
			normalizedColor = normalizedColor.split("").map((char) => char + char).join("")
		}
		if(normalizedColor.length !== 6){
			return null
		}
		return {
			r: parseInt(normalizedColor.slice(0, 2), 16),
			g: parseInt(normalizedColor.slice(2, 4), 16),
			b: parseInt(normalizedColor.slice(4, 6), 16),
		}
	}

	parseRgbColorToRgb(color) {
		let rgbMatch = color.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/)
		if(!rgbMatch){
			return null
		}
		return {
			r: this.clampRgbChannel(Number(rgbMatch[1])),
			g: this.clampRgbChannel(Number(rgbMatch[2])),
			b: this.clampRgbChannel(Number(rgbMatch[3])),
		}
	}

	clampRgbChannel(value) {
		return Math.max(0, Math.min(255, Math.round(value)))
	}

	getDirectionalLineShortening(lineCount = 1, offsets = [], arrowSize = 0) {
		if(lineCount <= 1){
			return 0
		}
		let maxOffset = offsets.reduce((res, offset) => Math.max(res, Math.abs(offset)), 0)
		return maxOffset + arrowSize / 2
	}

	drawDirectionalArrow(context, length, connectionStyle = {}, offsetY = 0) {
		context.save()
		context.beginPath()
		let arrowSize = this.getDirectionalArrowSize(connectionStyle)
		context.moveTo(length - arrowSize, offsetY + arrowSize)
		context.lineTo(length, offsetY)
		context.moveTo(length - arrowSize, offsetY - arrowSize)
		context.lineTo(length, offsetY)
		context.stroke()
		context.restore()
	}

	drawStarPath(context, cx, cy, outerRadius, innerRadius) {
		let rotation = Math.PI / 2 * 3
		let step = Math.PI / 5
		context.moveTo(cx, cy - outerRadius)
		for(let index = 0; index < 5; index++){
			context.lineTo(cx + Math.cos(rotation) * outerRadius, cy + Math.sin(rotation) * outerRadius)
			rotation += step
			context.lineTo(cx + Math.cos(rotation) * innerRadius, cy + Math.sin(rotation) * innerRadius)
			rotation += step
		}
		context.lineTo(cx, cy - outerRadius)
		context.closePath()
	}

	drawHeartPath(context, x, y, scale = 1) {
		context.moveTo(x, y + 5 * scale)
		context.bezierCurveTo(x - 6 * scale, y + 1 * scale, x - 9 * scale, y - 5 * scale, x, y - 2 * scale)
		context.bezierCurveTo(x + 9 * scale, y - 5 * scale, x + 6 * scale, y + 1 * scale, x, y + 5 * scale)
	}
}
